import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Row, Col, Divider, Select, Tabs, Badge, Tag, Button, Table } from 'antd';
import { 
  HeartOutlined, 
  MedicineBoxOutlined, 
  ClockCircleOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { 
  StyledInput,
  StyledInputNumber, 
  StyledTextArea,
  StyledSelect,
  SectionTitle,
  FormSection 
} from '../styled';
import MultipleReadingsTab from './MultipleReadingsTab';
import { EGFR_MIN, EGFR_MAX, EGFR_RANGE_MESSAGE } from '../utils/constants';
import UserManagementService from '../../../../services/UserManagementService';
import { useStore } from '../../../../hooks/storeContext';

import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../../constants/routes';

const MedicalTestingTab = ({ form, patient, currentVisit, onVisitContextChange, onReadingsChange, recordsMode = false }) => {
  const [readingsCount, setReadingsCount] = useState(0);
  const readingsRef = useRef(null); // control MultipleReadingsTab add form
  const [isHistoricalDay, setIsHistoricalDay] = useState(false);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedVisitId, setSelectedVisitId] = useState('current');
  const [visits, setVisits] = useState([]);
  const previousSelectedVisitIdRef = useRef('current');
  const [teamMembers, setTeamMembers] = useState([]);
  const { authStore } = useStore();
  // Cache for current visit form field values when user views a historical visit
  const currentVisitCacheRef = useRef(null);
  const initializedRef = useRef(false);

  // Memoize current visit ID to prevent unnecessary re-renders
  const currentVisitId = useMemo(() => {
    return currentVisit?.visitId || patient?.visitId || 'current';
  }, [currentVisit?.visitId, patient?.visitId]);
  
  // Allow editing any visit - completed visits can be edited for corrections
  // (audit trail is maintained via saveHistory)
  const isReadOnly = false;
  const canAddHistoricalReadings = recordsMode;
  const allowPlaceholders = true;

  // Waist-to-Height Ratio (WHtR) risk interpretation for the Body Measurements tag
  const waistToHeightRatio = Form.useWatch('waistToHeightRatio', form);
  const whtrRisk = useMemo(() => {
    const n = parseFloat(waistToHeightRatio);
    if (!n || isNaN(n) || n <= 0) return null;
    if (n < 0.50) return { label: 'Healthy', color: 'green' };
    if (n < 0.60) return { label: 'Increased Risk', color: 'orange' };
    return { label: 'High Risk', color: 'red' };
  }, [waistToHeightRatio]);

  // ApoB estimate from Non-HDL (Total − HDL) shown when no measured ApoB is
  // entered. Non-HDL cutoffs: <130 normal, 130–159 borderline, ≥160 high.
  const apoBWatch = Form.useWatch('apolipoproteinB', form);
  const totalCholWatch = Form.useWatch('totalCholesterol', form);
  const hdlWatch = Form.useWatch('hdlCholesterol', form);
  const apoBEstimate = useMemo(() => {
    const measured = parseFloat(apoBWatch);
    if (!isNaN(measured) && apoBWatch !== '' && apoBWatch != null) return null;
    const tc = parseFloat(totalCholWatch);
    const hdl = parseFloat(hdlWatch);
    if (isNaN(tc) || isNaN(hdl)) return null;
    const nonHdl = Math.round(tc - hdl);
    if (nonHdl < 0) return null;
    return nonHdl;
  }, [apoBWatch, totalCholWatch, hdlWatch]);

  // Fetch team members (non-volunteer) for blood test handler dropdown
  useEffect(() => {
    if (!recordsMode) return;
    const fetchTeamMembers = async () => {
      try {
        const [admins, users] = await Promise.all([
          UserManagementService.getUsers({ role: 'admin', limit: 100 }),
          UserManagementService.getUsers({ role: 'user', limit: 100 })
        ]);
        const allMembers = [...(admins.data || []), ...(users.data || [])];
        setTeamMembers(allMembers);
      } catch (err) {
        console.error('Failed to load team members:', err);
      }
    };
    fetchTeamMembers();
  }, [recordsMode]);

  // Initialize visits and current visit - only run once on mount or when patient changes
  useEffect(() => {
    // Try to get medicalReadings from form first, then from patient data
    let medicalReadings = form.getFieldValue('medicalReadings') || [];
    
    // If form doesn't have it yet, try patient data
    if (medicalReadings.length === 0 && patient?.screeningData?.medicalReadings) {
      medicalReadings = patient.screeningData.medicalReadings;
    }
    
    // Only update visits if the data actually changed
    if (JSON.stringify(visits) !== JSON.stringify(medicalReadings)) {
      setVisits(medicalReadings);
    }
    
    // Only set selectedVisitId to currentVisitId on initial load or if selectedVisitId is invalid
    if (recordsMode) {
      // In records mode always pin to the provided currentVisitId (external dropdown controls it)
      setSelectedVisitId(currentVisitId);
    } else if (!initializedRef.current || (selectedVisitId === 'current' || !medicalReadings.find(v => v.visitId === selectedVisitId))) {
      setSelectedVisitId(currentVisitId);
    }
    
    const currentVisitData = medicalReadings.find(v => v.visitId === currentVisitId);
    const newReadingsCount = currentVisitData?.vitalSigns?.length || 0;
    if (readingsCount !== newReadingsCount) {
      setReadingsCount(newReadingsCount);
    }
    // Determine if selected/current visit is a historical date (for Add button state)
    try {
      const visitDate = currentVisitData?.visitDate ? moment(currentVisitData.visitDate) : moment();
      setIsHistoricalDay(!visitDate.isSame(moment(), 'day'));
    } catch(e) { setIsHistoricalDay(false); }
    
    if (!initializedRef.current) {
      initializedRef.current = true;
    }
  }, [patient?.screeningData?.medicalReadings, currentVisitId]); // Only depend on actual data, not the patient object itself

  // Load visit data when switching tabs - only when selectedVisitId changes
  useEffect(() => {
  if (recordsMode) return; // switching handled externally by remount
  if (!initializedRef.current) return;
  if (previousSelectedVisitIdRef.current === selectedVisitId) return; // no change

    const visitFieldNames = [
      'systolicBP','diastolicBP','heartRate',
      'height','weight','waistCircumference','hipCircumference','whr','waistToHeightRatio','subcutaneousFat','visceralFat',
      'totalCholesterol','hdlCholesterol','ldlCholesterol','triglycerides','bloodGlucose','a1c','lipoproteinA','apolipoproteinB','apolipoproteinA1','creatinine','cReactiveProtein','fastingInsulin','psa','eGFR',
      'heartRhythm','qrsDuration','qtInterval','ekgInterpretation','ekgAbnormalities',
      'clinicalNotes','recommendations'
    ];

    const from = previousSelectedVisitIdRef.current;
    const to = selectedVisitId;

    // Switching away from current visit -> cache current form values
    if (from === currentVisitId && to !== currentVisitId) {
      currentVisitCacheRef.current = form.getFieldsValue(visitFieldNames);
    }

    if (to !== currentVisitId) {
      // Load historical visit values
      const historicalVisit = visits.find(v => v.visitId === to);
      if (historicalVisit) {
        loadHistoricalVisitData(historicalVisit);
      }
    } else if (from !== currentVisitId) {
      // Returning to current visit -> restore cached values if present
      if (currentVisitCacheRef.current) {
        form.setFieldsValue(currentVisitCacheRef.current);
      }
      // If no cache (edge case), leave existing user input untouched
    }

    previousSelectedVisitIdRef.current = to;
  }, [selectedVisitId, currentVisitId, form, visits]);

  // Notify parent of visit context (historical vs current)
  useEffect(() => {
    if (typeof onVisitContextChange === 'function') {
      try {
        onVisitContextChange({
          isHistorical: selectedVisitId !== currentVisitId,
          selectedVisitId,
          currentVisitId
        });
      } catch(e) { /* noop */ }
    }
  }, [selectedVisitId, currentVisitId, onVisitContextChange]);

  const loadHistoricalVisitData = (visit) => {
    const latestVitalSign = visit.vitalSigns?.[visit.vitalSigns.length - 1];
    const latestBloodTest = visit.bloodTests?.[0];
    const latestEkgTest = visit.ekgTests?.[0];
    const bodyMeasurements = visit.bodyMeasurements || {};
    
    console.log('📦 Loading historical body measurements:', {
      visitId: visit.visitId,
      rawBodyMeasurements: visit.bodyMeasurements,
      heightInches: bodyMeasurements.heightInches,
      weightLbs: bodyMeasurements.weightLbs,
      waistInches: bodyMeasurements.waistInches,
      hipInches: bodyMeasurements.hipInches,
      whr: bodyMeasurements.whr
    });
    
    form.setFieldsValue({
      // Vital Signs
      systolicBP: latestVitalSign?.systolicBP,
      diastolicBP: latestVitalSign?.diastolicBP,
      heartRate: latestVitalSign?.heartRate,
      
      // Body Measurements (FIXED: Use correct property names from backend)
      height: bodyMeasurements.heightInches,
      weight: bodyMeasurements.weightLbs,
      waistCircumference: bodyMeasurements.waistInches,
      hipCircumference: bodyMeasurements.hipInches,
      whr: bodyMeasurements.whr,
      waistToHeightRatio: bodyMeasurements.waistToHeightRatio,
      subcutaneousFat: bodyMeasurements.subcutaneousFat,
      visceralFat: bodyMeasurements.visceralFat,
      
      // Blood Tests
      totalCholesterol: latestBloodTest?.totalCholesterol,
      hdlCholesterol: latestBloodTest?.hdlCholesterol,
      ldlCholesterol: latestBloodTest?.ldlCholesterol,
      triglycerides: latestBloodTest?.triglycerides,
      bloodGlucose: latestBloodTest?.bloodGlucose,
      a1c: latestBloodTest?.a1c,
      lipoproteinA: latestBloodTest?.lipoproteinA,
      apolipoproteinB: latestBloodTest?.apolipoproteinB,
      apolipoproteinA1: latestBloodTest?.apolipoproteinA1,
      creatinine: latestBloodTest?.creatinine,
      cReactiveProtein: latestBloodTest?.cReactiveProtein,
      fastingInsulin: latestBloodTest?.fastingInsulin,
      psa: latestBloodTest?.psa,
      eGFR: latestBloodTest?.eGFR,
      
      // EKG Results
      heartRhythm: latestEkgTest?.heartRhythm,
      qrsDuration: latestEkgTest?.qrsDuration,
      qtInterval: latestEkgTest?.qtInterval,
      ekgInterpretation: latestEkgTest?.interpretation,
      ekgAbnormalities: latestEkgTest?.abnormalities?.join(', '),
      
      // Clinical Notes
      clinicalNotes: visit.clinicalNotes,
      recommendations: visit.recommendations
    });
  };

  // Blood pressure reading capture removed from this tab (moved to BP Readings)

  // Build tab items for visit selector
  const existingVisitIds = new Set(visits.map(v => v.visitId));
  const isExistingAsHistorical = existingVisitIds.has(currentVisitId);
  const visitTabs = [
    {
      key: currentVisitId,
      label: (() => {
        const v = visits.find(v => v.visitId === currentVisitId);
        if (isExistingAsHistorical) {
          return (
            <span style={{ display:'flex', alignItems:'center', gap:8, fontSize:'15px', padding:'4px 8px' }}>
              <ClockCircleOutlined style={{ color:'#8c8c8c', fontSize:16 }} />
              <span style={{ fontWeight:500 }}>{v?.visitDate ? moment(v.visitDate).format('MMM DD, YYYY') : 'Visit'}</span>
              {!recordsMode && (
                <Tag color="blue" style={{ fontSize:11, margin:0, display:'inline-flex', alignItems:'center' }}>Selected</Tag>
              )}
              {recordsMode && (
                <Tag color="green" style={{ fontSize:11, margin:0, display:'inline-flex', alignItems:'center' }}>Editable</Tag>
              )}
            </span>
          );
        }
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', padding: '4px 8px' }}>
            <HeartOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
            <span style={{ fontWeight: 500 }}>Current Visit</span>
            {!recordsMode && (
              <Badge count="Active" style={{ backgroundColor: '#52c41a', fontSize: '11px' }} />
            )}
          </span>
        );
      })()
    },
    ...visits
      .filter(v => v.visitId !== currentVisitId)
      .filter(v => {
        // Filter out visits from today - they're represented by the "Current Visit" tab
        if (v.visitDate) {
          const visitMoment = moment(v.visitDate);
          if (visitMoment.isSame(moment(), 'day')) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate))
      .map(visit => ({
        key: visit.visitId,
        label: (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', padding: '4px 8px' }}>
            <ClockCircleOutlined style={{ color: '#8c8c8c', fontSize: '16px' }} />
            <span style={{ fontWeight: 500 }}>{moment(visit.visitDate).format('MMM DD, YYYY')}</span>
            <Tag color="green" style={{ fontSize: '11px', margin: 0, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              Editable
            </Tag>
          </span>
        )
      }))
  ];

  return (
    <>
      {/* Visit Selector Tabs - hidden in records mode (external dropdown used) */}
      {!recordsMode && visits.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {/* Local style override: remove bottom border line for active Current Visit tab only */}
          <style>{`
            /* Remove underline on active current visit tab (card mode adds bottom border) */
            .visit-tabs.ant-tabs-card > .ant-tabs-nav .ant-tabs-nav-list > .ant-tabs-tab:first-child.ant-tabs-tab-active {
              border-bottom: 0 !important;
              border-bottom-color: transparent !important;
              box-shadow: none !important;
            }
            /* Hide ink bar for this tab set */
            .visit-tabs .ant-tabs-ink-bar { display: none !important; }
            /* No alignment override (explicitly unset to avoid prior injected rules) */
            .visit-tabs.ant-tabs-card > .ant-tabs-nav .ant-tabs-nav-list { align-items: unset !important; }
            /* Light shadow for any active visit tab */
            .visit-tabs.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active {
              box-shadow: 0 2px 6px rgba(0,0,0,0.09) !important;
              background: #ffffff !important;
              position: relative;
              z-index: 1;
            }
          `}</style>
          <Tabs
            className="visit-tabs"
            activeKey={selectedVisitId}
            onChange={setSelectedVisitId}
            items={visitTabs}
            type="card"
            size="large"
            tabBarStyle={{
              marginBottom: 0,
              fontSize: '15px'
            }}
            tabBarGutter={12}
            style={{
              fontSize: '15px'
            }}
          />
          
          {/* Visit Context Info Bar - Shows for historical visits */}
          {selectedVisitId !== currentVisitId && !recordsMode && (
            <div style={{ 
              marginTop: 16,
              padding: '16px 24px',
              backgroundColor: '#f0f7ff',
              borderLeft: '4px solid #1890ff',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                  <span style={{ fontWeight: 500 }}>
                    {moment(visits.find(v => v.visitId === selectedVisitId)?.visitDate).format('MMM DD, YYYY')}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <EnvironmentOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                  <span>{visits.find(v => v.visitId === selectedVisitId)?.location || 'Unknown'}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#8c8c8c' }}>
                    {visits.find(v => v.visitId === selectedVisitId)?.eventName || 'Unknown'}
                  </span>
                </div>
              </div>
              <Button size="small" type="primary" onClick={() => {
                const pid = patient?.patientId || patient?.patient?._id || patient?._id;
                if (pid) navigate(ROUTES.ADMIN_MEMBER_RECORD_EDIT.replace(':patientId', pid));
              }}>Edit This Member</Button>
            </div>
          )}
        </div>
      )}

      {/* Visit History Summary moved to dedicated "Visit History" tab */}

      <FormSection>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:12, flexWrap:'wrap' }}>
          <style>
            {`
            /* Ensure ghost primary buttons remain visible and transparent */
            .ghost-primary-visible.ant-btn-background-ghost,
            .ghost-primary-visible.ant-btn-ghost {
              color: #1677ff !important;
              border-color: #1677ff !important;
              background: transparent !important;
            }
            .ghost-primary-visible.ant-btn-background-ghost:hover,
            .ghost-primary-visible.ant-btn-background-ghost:focus,
            .ghost-primary-visible.ant-btn-ghost:hover,
            .ghost-primary-visible.ant-btn-ghost:focus {
              color: #1677ff !important;
              border-color: #1677ff !important;
              background: rgba(22,119,255,0.06) !important;
            }
            `}
          </style>
          <SectionTitle style={{ marginBottom: 0 }}>
            <MedicineBoxOutlined />
            Vital Signs (Blood Pressure & Heart Rate)
          </SectionTitle>
          <Button
            type="default"
            ghost
            onClick={() => readingsRef.current?.toggleAddForm?.()}
            disabled={isReadOnly || (isHistoricalDay && !canAddHistoricalReadings)}
            className="ghost-primary-visible"
            style={{ background: 'transparent', boxShadow: 'none' }}
            data-qa="bp-header-ghost-btn"
          >
            {isAddFormOpen ? 'Cancel' : 'Add BP Reading'}
          </Button>
        </div>
        {isReadOnly ? (
          (() => {
            const historicalVisit = visits.find(v => v.visitId === selectedVisitId);
            const vitalSigns = historicalVisit?.vitalSigns || [];
            const columns = [
              { title: 'Time', dataIndex: 'timestamp', key: 'time', width: 130, render: (t) => t ? moment(t).format('MM/DD/YYYY h:mm A') : '—' },
              { title: 'Blood Pressure', key: 'bp', width: 140, render: (r) => r.systolicBP && r.diastolicBP ? `${r.systolicBP}/${r.diastolicBP}` : '—' },
              { title: 'Heart Rate', key: 'hr', width: 120, render: (r) => r.heartRate ? `${r.heartRate} bpm` : '—' },
              { title: '#', dataIndex: 'readingNumber', key: 'readingNumber', width: 60 }
            ];
            return (
              <div style={{ background:'#fff', border:'1px solid #f0f0f0', padding:'12px 16px 12px', borderRadius:6, marginTop:4, display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ fontSize:13, color:'#595959' }}>Historical readings for this visit (view only)</div>
                <Table size="small" columns={columns} dataSource={vitalSigns.map((r,i)=> ({ key:r.id || r._id || i, ...r }))} pagination={false} locale={{ emptyText: 'No BP readings recorded for this visit.' }} style={{ background:'#fff', marginBottom:0 }} scroll={{ x: 'max-content' }} />
              </div>
            );
          })()
        ) : (
          <div style={{ marginTop:4 }}>
            <MultipleReadingsTab
              hideTitle
              hideInlineAddButton
              form={form}
              patient={patient}
              currentVisit={currentVisit}
              initialReadings={visits}
              allowHistoricalReadings={canAddHistoricalReadings}
              ref={readingsRef}
              onNewReadingAdded={(newReading) => {
                if (typeof onReadingsChange === 'function') {
                  try {
                    onReadingsChange({ type: 'added', reading: newReading });
                  } catch(e) { /* noop */ }
                }
              }}
              onReadingsUpdated={(updatedReadings)=>{
                // Notify parent that readings changed (for dirty tracking in records mode)
                console.log('[MedicalTestingTab] onReadingsUpdated called, length=', updatedReadings?.length);
                if (typeof onReadingsChange === 'function') {
                  try { 
                    console.log('[MedicalTestingTab] Calling onReadingsChange');
                    onReadingsChange({ type: 'updated', readings: updatedReadings }); 
                  } catch(e) { /* noop */ }
                }
              }}
              onAddFormVisibilityChange={setIsAddFormOpen}
            />
          </div>
        )}
      </FormSection>

      <Divider />

      <FormSection>
        <SectionTitle>
          <MedicineBoxOutlined />
          Testing Code
        </SectionTitle>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Testing Code"
              name="testingCode"
              tooltip="The code assigned during bus screening for quick reference."
            >
              <StyledInput 
                placeholder={allowPlaceholders ? "Enter testing code" : undefined}
                disabled={isReadOnly}
                onChange={(e) => {
                  const val = e.target.value;
                  // Auto-set blood test handler to current user when testing code is entered
                  if (val && recordsMode && authStore?.me?._id) {
                    form.setFieldsValue({ bloodTestHandler: authStore.me._id });
                  }
                }}
              />
            </Form.Item>
          </Col>
          {recordsMode && (
            <Col xs={24} md={8}>
              <Form.Item
                label="Blood Test Processed By"
                name="bloodTestEnteredBy"
                tooltip="The team member who processed/entered the blood test."
              >
                <StyledSelect
                  placeholder="Select team member"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  disabled={isReadOnly}
                >
                  {teamMembers.map(member => (
                    <StyledSelect.Option key={member._id} value={member._id}>
                      {member.fullName || member.email}
                    </StyledSelect.Option>
                  ))}
                </StyledSelect>
              </Form.Item>
            </Col>
          )}
          {recordsMode && (
            <Col xs={24} md={8}>
              <Form.Item
                label="Blood Test Sent By"
                name="bloodTestHandler"
                tooltip="The team member who sent off the blood test."
              >
                <StyledSelect
                  placeholder="Select team member"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  disabled={isReadOnly}
                >
                  {teamMembers.map(member => (
                    <StyledSelect.Option key={member._id} value={member._id}>
                      {member.fullName || member.email}
                    </StyledSelect.Option>
                  ))}
                </StyledSelect>
              </Form.Item>
            </Col>
          )}
        </Row>
      </FormSection>

      <Divider />

      <FormSection>
        <SectionTitle>
          <HeartOutlined />
          Blood Tests — Finger Prick
        </SectionTitle>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Total Cholesterol (mg/dL)"
              name="totalCholesterol"
              tooltip="Total cholesterol level in blood. Desirable: <200, Borderline high: 200-239, High: ≥240 mg/dL. Higher levels increase heart disease risk."
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "200" : undefined} 
                min={50} 
                max={500}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="HDL Cholesterol (mg/dL)"
              name="hdlCholesterol"
              tooltip="'Good' cholesterol that helps remove other cholesterol from arteries. Higher is better: Men ≥40, Women ≥50 mg/dL. Low HDL increases heart disease risk."
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "50" : undefined} 
                min={10} 
                max={200}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="LDL Cholesterol (mg/dL)"
              name="ldlCholesterol"
              tooltip="'Bad' cholesterol that builds up in arteries. Lower is better: Optimal <100, Near optimal 100-129, Borderline high 130-159, High ≥160 mg/dL."
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "100" : undefined} 
                min={10} 
                max={400}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Triglycerides (mg/dL)"
              name="triglycerides"
              tooltip="Type of fat in blood. Normal <150, Borderline high 150-199, High 200-499, Very high ≥500 mg/dL. High levels increase heart disease risk."
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "150" : undefined} 
                min={10} 
                max={1000}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Lipoprotein(a) (nmol/L)"
              name="lipoproteinA"
              tooltip="Lp(a) is an independent cardiovascular risk factor. Elevated levels (≥75 nmol/L) increase heart disease risk. High risk: ≥125 nmol/L."
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "40" : undefined} 
                min={0} 
                max={500} 
                step={1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="ApoB (mg/dL)"
              name="apolipoproteinB"
              tooltip="Apolipoprotein B reflects the number of atherogenic (LDL) particles. Optimal <90, borderline 90-129, high ≥130 mg/dL. (Optional)"
              extra={apoBEstimate != null
                ? `Est. ${apoBEstimate} from Non-HDL (Total − HDL). Non-HDL cutoffs: <130 normal, 130–159 borderline, ≥160 high.`
                : undefined}
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "90" : undefined} 
                min={0} 
                max={300} 
                step={1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="ApoA1 (mg/dL)"
              name="apolipoproteinA1"
              tooltip="Apolipoprotein A1 is the main protein in HDL and is protective. Higher is better: typically >120 (men) / >140 (women) mg/dL. (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "140" : undefined} 
                min={0} 
                max={300} 
                step={1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Blood Glucose (mg/dL)"
              name="bloodGlucose"
              tooltip="Blood sugar level. Fasting: Normal <100, Prediabetes 100-125, Diabetes ≥126. Random: Normal <140, Prediabetes 140-199, Diabetes ≥200 mg/dL. (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "90" : undefined} 
                min={30} 
                max={500}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="A1C (%)"
              name="a1c"
              tooltip="Hemoglobin A1C measures average blood sugar over 2-3 months. Normal: <5.7%, Prediabetes: 5.7-6.4%, Diabetes: ≥6.5%. (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "5.5" : undefined} 
                min={3} 
                max={20} 
                step={0.1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="eGFR (mL/min/1.73m²)"
              name="eGFR"
              tooltip="Estimated Glomerular Filtration Rate — measure of kidney function. Normal ≥90, Mild reduction 60-89, Moderate (Stage 3a) 45-59, (Stage 3b) 30-44, Severe (Stage 4) 15-29, Kidney failure (Stage 5) <15."
              rules={[{ type: 'number', min: EGFR_MIN, max: EGFR_MAX, message: EGFR_RANGE_MESSAGE }]}
            >
              {/* No min/max props: antd would silently clamp out-of-range entries on blur */}
              <StyledInputNumber
                placeholder={allowPlaceholders ? "90" : undefined}
                step={1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Creatinine (mg/dL)"
              name="creatinine"
              tooltip="Serum creatinine, used to assess kidney function (and to calculate eGFR). Typical range ~0.6-1.3 mg/dL; higher values may indicate reduced kidney function. (Optional)"
            >
              <StyledInputNumber
                placeholder={allowPlaceholders ? "0.9" : undefined}
                min={0}
                max={20}
                step={0.01}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

      <Divider />

      <FormSection>
        <SectionTitle>
          <HeartOutlined />
          Blood Tests — Blood Draw
        </SectionTitle>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="PSA (ng/mL)"
              name="psa"
              tooltip="Prostate-Specific Antigen. Normal ranges vary by age: 40-49: 0-2.5, 50-59: 0-3.5, 60-69: 0-4.5, 70+: 0-6.5 ng/mL. Values >4 may need follow-up."
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "4" : undefined} 
                min={0} 
                max={100} 
                step={0.1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Fasting Insulin (µIU/mL)"
              name="fastingInsulin"
              tooltip="Fasting insulin is the primary lab used to assess insulin sensitivity / resistance. Typical fasting range ~2-20 µIU/mL; higher values suggest insulin resistance (used with glucose to derive HOMA-IR). (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "8" : undefined} 
                min={0} 
                max={300} 
                step={0.1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="hs-CRP (mg/L)"
              name="cReactiveProtein"
              tooltip="High-Sensitivity C-Reactive Protein — inflammation marker for cardiovascular risk. Low risk <1.0, average 1.0-3.0, high >3.0 mg/L. (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "1.0" : undefined} 
                min={0} 
                max={50} 
                step={0.1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

      <Divider />

      <FormSection>
        <SectionTitle>
          <HeartOutlined />
          EKG/ECG Results
        </SectionTitle>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Heart Rhythm"
              name="heartRhythm"
              tooltip="Select the heart rhythm type observed on EKG"
            >
              <Select 
                placeholder={allowPlaceholders ? "Select rhythm" : undefined}
                disabled={isReadOnly}
                allowClear
              >
                <Select.Option value="normal_sinus">Normal Sinus Rhythm</Select.Option>
                <Select.Option value="atrial_fibrillation">Atrial Fibrillation</Select.Option>
                <Select.Option value="atrial_flutter">Atrial Flutter</Select.Option>
                <Select.Option value="ventricular_tachycardia">Ventricular Tachycardia</Select.Option>
                <Select.Option value="bradycardia">Bradycardia</Select.Option>
                <Select.Option value="other">Other</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="QRS Duration (ms)"
              name="qrsDuration"
              tooltip="Normal QRS duration is typically 80-100 ms. Prolonged QRS (>120 ms) may indicate conduction abnormalities."
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "90" : undefined} 
                min={50} 
                max={300}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="QT Interval (ms)"
              name="qtInterval"
              tooltip="QT interval measures ventricular depolarization and repolarization. Normal range varies by heart rate."
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "400" : undefined} 
                min={200} 
                max={700}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="EKG Interpretation"
              name="ekgInterpretation"
              tooltip="Overall EKG reading and any abnormalities noted"
            >
              <StyledTextArea 
                placeholder={allowPlaceholders ? "Normal EKG, no acute changes..." : undefined}
                rows={3}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="EKG Abnormalities"
              name="ekgAbnormalities"
              tooltip="List any specific abnormalities found on EKG"
            >
              <StyledTextArea 
                placeholder={allowPlaceholders ? "None noted, or list specific findings..." : undefined}
                rows={3}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

      <Divider />

      <FormSection>
        <SectionTitle>
          <MedicineBoxOutlined />
          Body Measurements
          {whtrRisk && (
            <Tag
              color={whtrRisk.color}
              style={{ marginLeft: 10, fontWeight: 600 }}
            >
              {whtrRisk.label} · WHtR {Number(waistToHeightRatio).toFixed(2)}
            </Tag>
          )}
        </SectionTitle>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Height (inches)"
              name="height"
              tooltip="Patient height in inches. Used for calculating body composition metrics and medication dosing. Average adult height ranges from 60-78 inches. (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "68" : undefined} 
                min={36} 
                max={96}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Waist Circumference (inches)"
              name="waistCircumference"
              tooltip="Measured at the narrowest part of the waist, usually just above the belly button. Used with height to calculate the Waist-to-Height Ratio (WHtR) for cardiovascular risk assessment. (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "32" : undefined} 
                min={20} 
                max={80} 
                step={0.1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="WHtR (Waist-to-Height Ratio)"
              name="waistToHeightRatio"
              tooltip="Auto-calculated from waist ÷ height when both are entered. If you already have the ratio, you can type it in directly instead. Below 0.50 = healthy range, 0.50–0.59 = increased risk, 0.60 and above = high risk. (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "Auto-calculated or enter manually" : undefined} 
                min={0}
                max={2}
                step={0.01}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Subcutaneous Fat (mm)"
              name="subcutaneousFat"
              tooltip="Subcutaneous fat thickness measured via skinfold calipers. Typical range varies by site and gender. Used to assess body fat distribution. (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "12" : undefined} 
                min={0} 
                max={100} 
                step={0.1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Visceral Fat"
              name="visceralFat"
              tooltip="Visceral fat rating from a BIA body composition scale (e.g. Oxiline Scale MD Pro). 1-9: Normal, 10-14: High, 15+: Very High / Excess Visceral Fat. (Optional)"
            >
              <StyledInputNumber 
                placeholder={allowPlaceholders ? "e.g. 8" : undefined} 
                min={1} 
                max={59} 
                step={1}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormSection>

      <Divider />

      <FormSection>
        <SectionTitle>
          Assessment & Recommendations
        </SectionTitle>

        <Form.Item
          label="Clinical Notes"
          name="clinicalNotes"
          tooltip="Document any additional observations, patient concerns, or clinical findings noted during the screening examination."
        >
          <StyledTextArea 
            placeholder="Enter any additional observations or notes..."
            rows={8}
            disabled={isReadOnly}
          />
        </Form.Item>

        <Form.Item
          label="Recommendations"
          name="recommendations"
          tooltip="Provide specific health recommendations based on screening results, including lifestyle changes, follow-up care, or referrals to specialists."
        >
          <StyledTextArea 
            placeholder="Enter recommendations for the patient..."
            rows={6}
            disabled={isReadOnly}
          />
        </Form.Item>
      </FormSection>

      {/* Trends modal removed; trends now live in dedicated tab */}
    </>
  );
};

export default MedicalTestingTab;
